import { Singleton } from '@/shared/@cactus/web/classes/singleton';
import type { KycOperatorName } from '@/shared/Kyc/types';
import { isValidKycOperator } from '@/shared/@cactus/web/utils/kyc';
import { useApiService } from '@/composables/useApiService';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';
import { useLayoutStore } from '@/stores/layout';
import { ContainmentInstance } from '@/shared/Containment';
import type { RecoveryPasswordFlow } from '~/types/UserData';

interface CheckStatusOptions {
  once?: boolean;
}

interface Options {
  maximumPoolingRetries: number;
  poolingDelayInMs: number;
}

export enum KycStatus {
  PROCESSING = 0,
  APPROVED = 1,
  REPROVED = 2,
  MANUAL_APPROVED = 3,
  MANUAL_APPROVE_PENDING = 4,
  MANUAL_REPROVED = 5,
  LIVENESS_PENDING = 6,
}

export class BaseKyc extends Singleton {
  static #options: Options = {
    maximumPoolingRetries: 20,
    poolingDelayInMs: 1000 * 2,
  };

  protected static currentOperator: Ref<KycOperatorName | null> = ref(null);

  protected static iframeSrc: Ref<string> = ref('');
  protected static isDialogVisible: Ref<boolean> = ref(false);
  protected static isKycLoading: Ref<boolean> = ref(false);
  protected static isCheckStatusLoading: Ref<boolean> = ref(false);
  protected static isPoolingRunning: Ref<boolean> = ref(false);
  protected static internalFlow: Ref<Kyc.FlowType> = ref('default');
  protected static verificationStep: Ref<Kyc.VerificationStep> = ref('initial');

  protected static manualRetriesCount: number = 0;

  public iframePermissions: string =
    'geolocation;camera;fullscreen;accelerometer;gyroscope;magnetometer;';

  protected get options(): Options {
    return BaseKyc.#options;
  }

  public get status(): typeof KycStatus {
    return KycStatus;
  }

  public get iframeSrc(): string {
    return BaseKyc.iframeSrc.value;
  }

  public closeKycDialog(): void {
    BaseKyc.isDialogVisible.value = false;
  }

  public get isDialogVisible(): boolean {
    return BaseKyc.isDialogVisible.value;
  }

  protected setDialogVisibility(visibility: boolean) {
    BaseKyc.isDialogVisible.value = visibility;
  }

  public get isKycLoading(): boolean {
    return BaseKyc.isKycLoading.value;
  }

  public setKycLoading(isLoading: boolean) {
    BaseKyc.isKycLoading.value = isLoading;
  }

  public get isCheckStatusLoading(): boolean {
    return BaseKyc.isCheckStatusLoading.value;
  }

  public get isPoolingRunning(): boolean {
    return BaseKyc.isPoolingRunning.value;
  }

  public get internalFlow(): Kyc.FlowType {
    return BaseKyc.internalFlow.value;
  }

  public get verificationStep(): Kyc.VerificationStep {
    return BaseKyc.verificationStep.value;
  }

  public setVerificationStep(step: Kyc.VerificationStep) {
    BaseKyc.verificationStep.value = step;

    const stepsToResetKycOperator = [
      'expired',
      'finished',
      'validated',
      'failed',
      'internal_start_error',
      'internal_status_error',
    ];

    if (stepsToResetKycOperator.includes(step)) {
      this.resetKycOperator();
    }
  }

  public resetEnvironment() {
    debug('resetEnvironment ~ BASE');

    const { currentMessageIndex } = useKyc();

    BaseKyc.internalFlow.value = 'default';
    BaseKyc.verificationStep.value = 'initial';
    BaseKyc.isPoolingRunning.value = false;
    BaseKyc.isKycLoading.value = false;
    BaseKyc.iframeSrc.value = '';
    currentMessageIndex.value = 0;
  }

  private setKycOperator(operator: Kyc.Operator | undefined) {
    debug('Kyc operator is', operator);

    if (!operator || !isValidKycOperator(operator)) {
      return this.resetKycOperator();
    }

    BaseKyc.currentOperator.value = operator;
  }

  public resetKycOperator() {
    BaseKyc.currentOperator.value = null;
  }

  public get kycOperator(): KycOperatorName | null {
    return BaseKyc.currentOperator.value;
  }

  public async fetchIframeUrl() {
    debug('fetchIframeUrl ~ BASE');

    this.setKycLoading(true);

    const { isProcessing } = useKyc();

    if (isProcessing.value) {
      this.setKycLoading(false);

      return this.closeKycDialog();
    }

    const { t } = useTexts();
    const { openToaster } = useLayoutStore();
    const { openForgotPassword } = useAuth();
    const { resetRecoveryPasswordFlow } = useAuthStore();
    const { isAuthenticated, recoveryPasswordFlow } =
      storeToRefs(useAuthStore());

    const { kycId, kycSource } = useKyc();

    try {
      const { ApiService } = useApiService();

      const { forceRequestKycReason } = useUserData();

      if (forceRequestKycReason.value) {
        kycSource.value = forceRequestKycReason.value;
      }

      const resource = this.buildKycUrl(
        kycSource.value,
        isAuthenticated.value,
        recoveryPasswordFlow.value,
      );

      const { data } = await ApiService.get<Kyc.DataResponse>(resource);

      this.setKycOperator(data.operator);
      this.setVerificationStep('fetched');
      this.setKycLoading(false);

      if (data.exception) {
        throw new Error(data.exception);
      }

      if (data.status === KycStatus.MANUAL_APPROVE_PENDING) {
        BaseKyc.internalFlow.value = 'manual_approve_pending';

        return openToaster({
          message: t('verification.kyc.title_manual_approve_pending'),
          type: 'info',
        });
      }

      if (!data.url || !data.correlation_id) {
        openToaster({
          message: t('general.errors.service_unavailable'),
          type: 'error',
        });

        throw new Error(t('general.errors.service_unavailable'));
      }

      BaseKyc.iframeSrc.value = data.url;

      kycId.value = data.correlation_id;
    } catch (error: any) {
      console.debug('error', error);

      this.setKycLoading(false);

      this.setVerificationStep('internal_start_error');

      this.closeKycDialog();

      const message =
        error.data?.message ||
        error.message ||
        error.statusText ||
        error ||
        t('general.errors.service_unavailable');

      openToaster({
        message,
        type: 'error',
      });

      if (recoveryPasswordFlow.value?.token && error?.status === 403) {
        const containmentInstance = ContainmentInstance.getInstance();

        await containmentInstance.solve({
          name: 'kyc',
          customValidator: () => true,
        });

        resetRecoveryPasswordFlow();
        openForgotPassword();
      }
    }
  }

  public async openKycDialog(): Promise<void> {
    debug('openKycDialog ~ BASE');

    if (!BaseKyc.iframeSrc.value) {
      await this.fetchIframeUrl();
    }

    const { kycId } = useKyc();

    if (this.iframeSrc && kycId.value) {
      this.setDialogVisibility(true);
    }

    this.setKycLoading(true);
    setTimeout(() => {
      this.setKycLoading(false);
    }, 3000);
  }

  public async checkStatus(
    options: CheckStatusOptions = {},
    retryNumber: number = 1,
  ) {
    debug('checkStatus ~ BASE');

    const { kycId, nextLoadingMessage } = useKyc();

    if (!kycId.value) {
      alert('Erro ao realizar o KYC, por favor entre em contato com o suporte');

      this.setVerificationStep('initial');

      return;
    }

    if (retryNumber === 1) {
      // Attribution do KYC pro pipeline server-side: dispara no INÍCIO do
      // polling (momento do submit) — a APROVAÇÃO quem confirma é o core
      // via buffer (cobre aprovação manual/fora de sessão, que o evento
      // client-side antigo perdia). Re-submits colapsam na fila (dedupe).
      useUserJourney().submitKycEvent();
    }

    const { once = false } = options;

    this.setVerificationStep('checking');
    BaseKyc.isCheckStatusLoading.value = true;

    await sleep(this.options.poolingDelayInMs);

    const { t } = useTexts();
    const { openToaster } = useLayoutStore();
    const { isAuthenticated, recoveryPasswordFlow } =
      storeToRefs(useAuthStore());

    try {
      const { ApiService } = useApiService();

      const resource = this.buildKycStatusUrl(
        kycId.value,
        isAuthenticated.value,
        recoveryPasswordFlow.value,
      );

      const { data } = await ApiService.get<Kyc.StatusDataResponse>(resource);

      BaseKyc.isCheckStatusLoading.value = false;
      nextLoadingMessage();

      if (!String(data.status)) {
        this.setVerificationStep('initial');

        openToaster({
          message: t('general.errors.service_unavailable'),
          type: 'error',
        });

        throw new Error(t('general.errors.service_unavailable'));
      }

      const { status } = data;

      if (
        String(status) === 'APPROVED' ||
        status === KycStatus.APPROVED ||
        status === KycStatus.MANUAL_APPROVED
      ) {
        this.setVerificationStep('validated');

        const { access_token, recovery_token } = data;

        if (access_token) {
          const { setAuth } = useAuthStore();

          await setAuth({ data: { access_token: String(access_token) } });

          await sleep(1000);
        }

        if (recovery_token) {
          const { setRecoveryPasswordFlow } = useAuthStore();

          setRecoveryPasswordFlow({
            token: recovery_token,
          });
        }

        const { fetchUserProfile } = useUserData();

        await fetchUserProfile({ force: true });

        // kyc_approved client-side REMOVIDO: a conversão agora nasce
        // server-side (submit_kyc no collector → buffer → S2S), cobrindo
        // 100% das aprovações — manter o dispatch aqui duplicaria o evento
        // na AppsFlyer para aprovações em sessão.

        const containmentInstance = ContainmentInstance.getInstance();

        return await containmentInstance.solve({
          name: 'kyc',
          customValidator: () => true,
        });
      }

      if (
        String(status) === 'REPROVED' ||
        status === KycStatus.REPROVED ||
        status === KycStatus.MANUAL_REPROVED
      ) {
        this.setVerificationStep('failed');

        return openToaster({
          message: t('verification.kyc.failed'),
          type: 'error',
        });
      }

      if (retryNumber >= this.options.maximumPoolingRetries) {
        this.setVerificationStep('expired');

        return openToaster({
          message: t('verification.kyc.max_retries'),
          type: 'info',
        });
      }

      if (once) {
        this.setVerificationStep('manual_refresh');

        return;
      }

      BaseKyc.isPoolingRunning.value = true;

      await this.checkStatus({ ...options }, retryNumber + 1);
    } catch (error: any) {
      console.debug('error', error);

      this.setVerificationStep('internal_status_error');

      BaseKyc.isCheckStatusLoading.value = false;

      const message =
        error.data?.message ||
        error.message ||
        error.statusText ||
        error ||
        'Não foi possível validar sua selfie, tente novamente';

      openToaster({
        message,
        type: 'error',
        time: 12000,
        position: 'bottom-right',
      });

      if (error?.status === 422) {
        return this.setVerificationStep('internal_start_error');
      }
    }
  }

  private buildKycUrl(
    source: string,
    isAuthenticated: boolean,
    recoveryPasswordFlow: RecoveryPasswordFlow,
  ): string {
    let resource = '/bff/users/kyc';
    const urlParams = new URLSearchParams({ source });

    if (!isAuthenticated && recoveryPasswordFlow) {
      resource += '/recovery';
      urlParams.append('token', recoveryPasswordFlow.token);
    }

    return `${resource}?${urlParams.toString()}`;
  }

  private buildKycStatusUrl(
    kycId: number,
    isAuthenticated: boolean,
    recoveryPasswordFlow: RecoveryPasswordFlow,
  ): string {
    let resource = '/bff/users/kyc/status';
    const urlParams = new URLSearchParams({ kyc_id: String(kycId) });

    if (!isAuthenticated && recoveryPasswordFlow) {
      const { setRecoveryPasswordData } = useAuthStore();

      setRecoveryPasswordData({
        kyc_id: kycId,
      });

      resource += '/recovery';
      urlParams.append('token', recoveryPasswordFlow.token);
    }

    return `${resource}?${urlParams.toString()}`;
  }

  public async manualRefresh(): Promise<void> {
    await this.checkStatus({ once: true }, ++BaseKyc.manualRetriesCount);
  }

  public async listenPostMessage(message: MessageEvent) {
    if (!message.data) {
      return;
    }

    const data =
      typeof message.data === 'string'
        ? JSON.parse(message.data)
        : message.data;

    await sleep(1000);

    debug('listenPostMessage', typeof message.data, data);
  }
}
